// ABOUTME: Global search input component for the PimAppBar with grouped dropdown results
// ABOUTME: Searches configured hierarchies, renders results grouped by hierarchy with listName display

import {
    Box,
    CircularProgress,
    List,
    ListItem,
    Paper,
    Popper,
    SearchIcon,
    TextField,
    Typography,
} from '@ds/coolshop';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { redirect } from '@src/_new/utils/redirect';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useGlobalSearch } from '../hooks/useGlobalSearch';

export const GlobalSearchInput = () => {
    const { formatMessage } = useIntl();
    const { searchTerm, setSearchTerm, results, loading, error, open, setOpen } = useGlobalSearch();
    const anchorRef = useRef<HTMLDivElement>(null);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const itemRefs = useRef<(HTMLLIElement | null)[]>([]);

    const allItems = results.flatMap(group =>
        group.items.map(item => ({ ...item, hierarchyId: group.hierarchyId })),
    );

    const handleSelect = useCallback(
        (hierarchyId: string, nodeId: string) => {
            setOpen(false);
            redirect({ hierarchy: hierarchyId, node: nodeId });
        },
        [setOpen],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
                setOpen(false);
                setFocusedIndex(-1);
                return;
            }
            if (!open || allItems.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                const next = focusedIndex < allItems.length - 1 ? focusedIndex + 1 : 0;
                setFocusedIndex(next);
                itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                const prev = focusedIndex > 0 ? focusedIndex - 1 : allItems.length - 1;
                setFocusedIndex(prev);
                itemRefs.current[prev]?.scrollIntoView({ block: 'nearest' });
            } else if (e.key === 'Enter' && focusedIndex >= 0) {
                e.preventDefault();
                const item = allItems[focusedIndex];
                if (!item) return;
                handleSelect(item.hierarchyId, item.nodeId);
            }
        },
        [open, allItems, focusedIndex, handleSelect, setOpen],
    );

    useEffect(() => {
        setFocusedIndex(-1);
        itemRefs.current = [];
    }, [results]);

    const showPopper = open && !!(loading || error || results.length > 0 || searchTerm.length >= 2);

    let itemIndex = 0;

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box ref={anchorRef}>
                <TextField
                    size="small"
                    placeholder={formatMessage({
                        id: 'global-search.placeholder',
                        defaultMessage: 'Search...',
                    })}
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        if (searchTerm.length >= 2 && results.length > 0) {
                            setOpen(true);
                        }
                    }}
                    StartIcon={SearchIcon}
                    endAdornment={loading ? <CircularProgress size="small" /> : undefined}
                />
                <Popper open={showPopper} anchorEl={anchorRef.current} placement="bottom-start">
                    <Paper elevation={6} overflow="auto">
                        <Box zIndex={1300} maxHeight={400} minWidth={300} mt={0.5} overflow="auto">
                            {error && (
                                <Box p={2}>
                                    <Typography color="error">
                                        {formatMessage({
                                            id: 'global-search.error',
                                            defaultMessage: 'Search unavailable',
                                        })}
                                    </Typography>
                                </Box>
                            )}
                            {!error &&
                                !loading &&
                                results.length === 0 &&
                                searchTerm.length >= 2 && (
                                    <Box p={2}>
                                        <Typography color="textSecondary">
                                            {formatMessage({
                                                id: 'global-search.no-results',
                                                defaultMessage: 'No results',
                                            })}
                                        </Typography>
                                    </Box>
                                )}
                            {!error &&
                                results.map(group => {
                                    const groupItems = group.items.map(item => {
                                        const currentIndex = itemIndex;
                                        itemIndex += 1;
                                        return (
                                            <ListItem
                                                key={`${group.hierarchyId}-${item.nodeId}`}
                                                ref={el => {
                                                    itemRefs.current[currentIndex] = el;
                                                }}
                                                onClick={() =>
                                                    handleSelect(group.hierarchyId, item.nodeId)
                                                }
                                                cursor="pointer"
                                                selected={focusedIndex === currentIndex}
                                            >
                                                <Typography variant="body2">
                                                    {item.listName || item.code}
                                                </Typography>
                                            </ListItem>
                                        );
                                    });

                                    return (
                                        <Box key={group.hierarchyId}>
                                            <Box px={2} pt={1.5} pb={0.5} fontWeight="bold">
                                                <Typography variant="caption" color="textSecondary">
                                                    {group.hierarchyCode} ({group.totalCount})
                                                </Typography>
                                            </Box>
                                            <List>{groupItems}</List>
                                        </Box>
                                    );
                                })}
                        </Box>
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};
